import JSBI from 'jsbi';
import { ZERO } from '../../constants';
import { Percentage } from '../../utils';
import { FEE_DIVISOR } from './config';
import { Reserves } from './types';

interface GetOutputAmountParams {
  reserves: Reserves;
  poolFee: number;
  inputMint: string;
  outputMint: string;
  inputAmount: JSBI;
  slippage: Percentage;
}

interface GetInputAmountParams {
  reserves: Reserves;
  poolFee: number;
  inputMint: string;
  outputMint: string;
  outputAmount: JSBI;
  slippage: Percentage;
}

function getOutputAmount({
  reserves,
  poolFee = 0,
  slippage,
  inputMint,
  outputMint,
  inputAmount,
}: GetOutputAmountParams) {
  const inputPoolBalance = reserves.get(inputMint) || ZERO;
  const outputPoolBalance = reserves.get(outputMint) || ZERO;

  if (
    JSBI.equal(inputPoolBalance, ZERO) ||
    JSBI.equal(outputPoolBalance, ZERO)
  ) {
    return {
      amountIn: inputAmount,
      amountOut: ZERO,
      minAmountOut: ZERO,
      feeAmount: ZERO,
      notEnoughLiquidity: true,
      priceImpact: 0,
    };
  }

  const feeAmount = JSBI.divide(
    JSBI.multiply(JSBI.BigInt(poolFee), inputAmount),
    FEE_DIVISOR
  );

  const inputAmountLessFees = JSBI.subtract(inputAmount, feeAmount);

  const numerator = JSBI.multiply(inputAmountLessFees, outputPoolBalance);
  const denominator = JSBI.add(inputPoolBalance, inputAmountLessFees);

  const amountOut = JSBI.divide(numerator, denominator);
  const amountOutLessSlippage = slippage.getFor(amountOut);

  const finalPrice =
    +inputPoolBalance.toString() / +outputPoolBalance.toString();
  const newPrice = +inputAmount.toString() / +amountOut.toString();

  const priceImpact = (newPrice - finalPrice) / newPrice;

  return {
    notEnoughLiquidity: false,
    amountIn: inputAmount,
    amountOut,
    minAmountOut: amountOutLessSlippage,
    feeAmount,
    priceImpact,
  };
}

function getInputAmount({
  reserves,
  poolFee = 0,
  slippage,
  inputMint,
  outputMint,
  outputAmount,
}: GetInputAmountParams) {
  const inputPoolBalance = reserves.get(inputMint) || ZERO;
  const outputPoolBalance = reserves.get(outputMint) || ZERO;

  if (
    JSBI.equal(inputPoolBalance, ZERO) ||
    JSBI.equal(outputPoolBalance, ZERO)
  ) {
    return {
      amountIn: ZERO,
      amountOut: outputAmount,
      minAmountOut: outputAmount,
      feeAmount: ZERO,
      notEnoughLiquidity: true,
      priceImpact: 0,
    };
  }

  const numerator = JSBI.multiply(outputAmount, inputPoolBalance);
  const denominator = JSBI.subtract(outputPoolBalance, outputAmount);

  const amountIn = JSBI.divide(numerator, denominator);

  const feeAmount = JSBI.divide(
    JSBI.multiply(JSBI.BigInt(poolFee), amountIn),
    FEE_DIVISOR
  );

  const amountInWithFees = JSBI.add(amountIn, feeAmount);

  const slippageAmount = JSBI.divide(
    JSBI.multiply(outputAmount, slippage.numerator),
    slippage.denominator
  );

  const minAmountOut = JSBI.subtract(outputAmount, slippageAmount);

  const finalPrice =
    +inputPoolBalance.toString() / +outputPoolBalance.toString();
  const newPrice = +amountInWithFees.toString() / +outputAmount.toString();

  const priceImpact = (newPrice - finalPrice) / newPrice;

  return {
    notEnoughLiquidity: false,
    amountIn: amountInWithFees,
    amountOut: outputAmount,
    minAmountOut,
    feeAmount,
    priceImpact,
  };
}

export { getOutputAmount, getInputAmount };
